import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
    Calendar,
    Clock,
    Eye,
    ArrowLeft,
    ArrowRight,
    ChevronDown,
    List,
    ClipboardCheck,
    CalendarClock,
    ShieldCheck,
    Smartphone,
    Share2,
    Bookmark,
    BookmarkCheck,
    Link2,
    Check,
    Target,
    CircleCheck,
    FileText,
    Tag,
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import BlogCard from "./BlogCard";
import useHydrated from "@/hooks/useHydrated";
import useIsMobile from "@/hooks/useIsMobile";
import FaqSection from "./FaqSection";
import { WhatsAppIcon, XIcon, SITE_SOCIAL_LINKS } from "@/Components/Icons/SocialIcons";
import logo from "@images/logo.webp";
import ahmedAlsubaiePhoto from "@images/team/ahmed-alsubaie.webp";

// Real photos for known team members; any other author name falls back to
// a generated initials avatar so the template stays generic.
const KNOWN_AUTHOR_PHOTOS = {
    "أحمد السبيعي": ahmedAlsubaiePhoto,
};

const WHATSAPP_NUMBER = "966569691027";

const QUICK_FACTS = [
    { Icon: ClipboardCheck, label: "محتوى موثوق", sub: "مصادر موثوقة ودقيقة" },
    { Icon: CalendarClock, label: "تحديث دوري", sub: "نراجع المحتوى باستمرار" },
    { Icon: ShieldCheck, label: "مراجعة خبراء", sub: "تمت المراجعة بعناية" },
    { Icon: Smartphone, label: "قابل للتطبيق", sub: "خطوات عملية ومباشرة" },
];

// Small SVG "dashboard" mockup — a generic vector illustration (no stock
// graphics), reused as the decorative left-side panel of the hero fallback.
const DashboardMockup = () => (
    <svg viewBox="0 0 220 160" className="h-full w-full" aria-hidden="true">
        <rect x="2" y="2" width="216" height="156" rx="14" fill="#FFFFFF" />
        <circle cx="20" cy="18" r="3.5" fill="#EF4444" />
        <circle cx="32" cy="18" r="3.5" fill="#F59E0B" />
        <circle cx="44" cy="18" r="3.5" fill="#22C55E" />

        {/* Donut chart */}
        <circle
            cx="58"
            cy="70"
            r="26"
            fill="none"
            stroke="#E2E8F0"
            strokeWidth="12"
        />
        <circle
            cx="58"
            cy="70"
            r="26"
            fill="none"
            stroke="#2563EB"
            strokeWidth="12"
            strokeDasharray="103 163"
            strokeLinecap="round"
            transform="rotate(-90 58 70)"
        />
        <circle
            cx="58"
            cy="70"
            r="26"
            fill="none"
            stroke="#00D4FF"
            strokeWidth="12"
            strokeDasharray="45 163"
            strokeDashoffset="-103"
            strokeLinecap="round"
            transform="rotate(-90 58 70)"
        />

        {/* Bar chart */}
        <rect x="108" y="90" width="14" height="40" rx="4" fill="#DCEAFE" />
        <rect x="130" y="70" width="14" height="60" rx="4" fill="#93C5FD" />
        <rect x="152" y="50" width="14" height="80" rx="4" fill="#2563EB" />
        <rect x="174" y="35" width="14" height="95" rx="4" fill="#0F2E86" />

        {/* Text lines */}
        <rect x="20" y="112" width="60" height="6" rx="3" fill="#CBD5E1" />
        <rect x="20" y="124" width="40" height="6" rx="3" fill="#E2E8F0" />
    </svg>
);

// Generic city-skyline silhouette — abstract vector motif (not a specific
// real city photo), used to echo the brand's blue theme.
const SkylineSilhouette = () => (
    <svg
        viewBox="0 0 420 220"
        preserveAspectRatio="xMidYMax slice"
        className="h-full w-full"
        aria-hidden="true"
    >
        <g fill="#FFFFFF" opacity="0.14">
            <rect x="0" y="120" width="34" height="100" />
            <rect x="40" y="90" width="28" height="130" />
            <rect x="74" y="140" width="30" height="80" />
            <rect x="112" y="70" width="24" height="150" />
            <rect x="330" y="100" width="30" height="120" />
            <rect x="365" y="130" width="28" height="90" />
        </g>
        {/* Tall centered tower with a spire, echoing the region's skyline */}
        <g fill="#FFFFFF" opacity="0.9">
            <rect x="196" y="60" width="16" height="160" rx="2" />
            <path d="M196 60 L204 20 L212 60 Z" />
            <circle cx="204" cy="52" r="5" />
        </g>
        <g fill="#FFFFFF" opacity="0.16">
            <rect x="150" y="110" width="26" height="110" />
            <rect x="224" y="95" width="28" height="125" />
            <rect x="258" y="130" width="30" height="90" />
            <rect x="294" y="105" width="26" height="115" />
        </g>
    </svg>
);

// Branded hero shown for any post without a real uploaded thumbnail — a
// bold content panel with a large illustration, the article title, and a
// subtitle area so the title and image read as one cohesive hero section.
const BlogHeroFallback = ({ title, description }) => (
    <div className="relative flex h-full w-full items-center overflow-hidden rounded-[28px] bg-gradient-to-br from-[#0B1F66] via-[#0F2E86] to-primary-600 px-6 py-5 sm:px-10 lg:px-12">
        <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-16 -right-14 h-64 w-64 rounded-full bg-white/10 blur-3xl"
        />
        <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-16 -left-12 h-72 w-72 rounded-full bg-[#00D4FF]/20 blur-3xl"
        />
        <div className="relative z-10 grid w-full items-center gap-6 lg:grid-cols-[minmax(320px,44%)_minmax(0,56%)]">
            {/* Decorative mockup — dropped below `lg` since the fixed 16:9
                box doesn't have room to stack it with the text on narrow
                screens; the title/logo carry the hero there instead. */}
            <div className="relative mx-auto hidden w-full max-w-[320px] lg:block lg:max-w-none">
                <div className="overflow-hidden rounded-[32px] bg-white/10 p-4 shadow-2xl shadow-black/20 backdrop-blur-xl">
                    <DashboardMockup />
                </div>
            </div>

            <div className="text-center lg:text-right">
                <img
                    src={logo}
                    alt="Wiz Freelance"
                    className="mx-auto h-6 w-auto brightness-0 invert opacity-90 sm:h-8 lg:mx-0 lg:h-9"
                />
                <h2 className="mt-3 line-clamp-3 text-lg font-black leading-[1.2] text-white sm:mt-6 sm:text-3xl lg:text-[48px] lg:leading-[1.05]">
                    {title}
                </h2>
                {description && (
                    <p className="mx-auto mt-2 hidden max-w-xl text-sm text-white/80 sm:mt-4 sm:block sm:text-base lg:mx-0">
                        {description}
                    </p>
                )}
            </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 opacity-50">
            <SkylineSilhouette />
        </div>
    </div>
);

// Auto-generates a table of contents from whatever H2 headings exist in the
// rendered article body — works for any post's content, nothing hardcoded.
const TableOfContents = ({ containerRef }) => {
    const [headings, setHeadings] = useState([]);
    const [open, setOpen] = useState(true);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const nodes = Array.from(container.querySelectorAll("h2"));
        const items = nodes.map((node, index) => {
            if (!node.id) node.id = `section-${index + 1}`;
            return { id: node.id, text: node.textContent };
        });
        setHeadings(items);
    }, [containerRef]);

    if (headings.length < 2) return null;

    const VISIBLE_COUNT = 4;
    const hiddenCount = headings.length - VISIBLE_COUNT;
    const visibleHeadings = showAll ? headings : headings.slice(0, VISIBLE_COUNT);

    return (
        <div className="mb-8 rounded-2xl border border-gray-100 bg-gray-50/70 lg:sticky lg:top-28 lg:z-20 lg:shadow-sm">
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-right"
            >
                <span className="flex items-center gap-2 font-bold text-gray-900">
                    <List className="h-5 w-5 text-primary-600" />
                    محتويات المقال
                </span>
                <ChevronDown
                    className={`h-5 w-5 text-gray-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                    >
                        <ul className="space-y-1 px-5 pb-4">
                            {visibleHeadings.map((heading) => (
                                <li key={heading.id}>
                                    <a
                                        href={`#${heading.id}`}
                                        className="flex items-center justify-between gap-2 rounded-lg px-2 py-2 text-sm text-gray-600 transition-colors hover:bg-white hover:text-primary-600"
                                    >
                                        {heading.text}
                                        <ArrowLeft className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                        {hiddenCount > 0 && (
                            <div className="px-5 pb-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAll((prev) => !prev)}
                                    aria-expanded={showAll}
                                    className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50"
                                >
                                    {showAll ? "عرض أقل" : `المزيد (${hiddenCount})`}
                                    <ChevronDown
                                        className={`h-4 w-4 transition-transform duration-300 ${showAll ? "rotate-180" : ""}`}
                                    />
                                </button>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// Shown only when the post actually has key points authored for it (via the
// dashboard's blog editor) — nothing fabricated for posts without any.
const KeyPointsCallout = ({ points }) => {
    if (!points || points.length === 0) return null;

    return (
        <div className="mb-8 grid grid-cols-1 gap-6 rounded-2xl bg-primary-50/60 p-6 sm:grid-cols-[auto_1fr] sm:items-center sm:p-8">
            <Target
                className="mx-auto h-16 w-16 shrink-0 text-primary-500 sm:mx-0"
                strokeWidth={1.5}
            />
            <div>
                <h2 className="mb-3 font-bold text-gray-900">
                    أهم النقاط التي ستتعلمها
                </h2>
                <ul className="space-y-2">
                    {points.map((point, index) => (
                        <li
                            key={index}
                            className="flex items-start gap-2.5 text-sm text-gray-700"
                        >
                            <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />
                            <span>{point}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

const AuthorCard = ({ author }) => (
    <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
            <img
                src={author.avatar}
                alt={author.name}
                loading="lazy"
                className="h-14 w-14 shrink-0 rounded-full object-cover ring-4 ring-primary-50 shadow-sm"
            />
            <div className="min-w-0">
                <p className="truncate font-bold text-gray-900">{author.name}</p>
                <p className="truncate text-sm text-gray-500">{author.role}</p>
            </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
            {SITE_SOCIAL_LINKS.slice(0, 3).map(({ label, href, Icon }) => (
                <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-primary-200 hover:text-primary-600"
                >
                    <Icon className="h-4 w-4" />
                </a>
            ))}
        </div>

        <a
            href="/blogs"
            className="mt-4 flex w-full items-center justify-center rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:border-primary-200 hover:text-primary-600"
        >
            جميع المقالات
        </a>
    </div>
);

const GrowthCTA = () => (
    <div className="rounded-2xl bg-primary-600 p-6 text-center text-white">
        <h3 className="text-lg font-bold">هل تريد نمو أعمالك؟</h3>
        <p className="mt-2 text-sm text-primary-100">
            احصل على استشارة مجانية من خبرائنا الآن
        </p>
        <a
            href="/contact"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-white py-2.5 text-sm font-bold text-primary-700 transition-colors hover:bg-primary-50"
        >
            تواصل معنا الآن
            <ArrowLeft className="h-4 w-4" />
        </a>
    </div>
);

const ShareAndSaveCard = ({ title }) => {
    const [copied, setCopied] = useState(false);
    const [saved, setSaved] = useState(false);
    const [shareUrl, setShareUrl] = useState("");

    useEffect(() => {
        if (typeof window !== "undefined") {
            setShareUrl(window.location.href);
            try {
                const savedSlugs = JSON.parse(
                    localStorage.getItem("wiz_saved_articles") || "[]",
                );
                setSaved(savedSlugs.includes(window.location.pathname));
            } catch {
                setSaved(false);
            }
        }
    }, []);

    const toggleSave = () => {
        if (typeof window === "undefined") return;
        const path = window.location.pathname;
        let savedSlugs = [];
        try {
            savedSlugs = JSON.parse(
                localStorage.getItem("wiz_saved_articles") || "[]",
            );
        } catch {
            savedSlugs = [];
        }
        const next = saved
            ? savedSlugs.filter((slug) => slug !== path)
            : [...savedSlugs, path];
        localStorage.setItem("wiz_saved_articles", JSON.stringify(next));
        setSaved(!saved);
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // clipboard API unavailable — silently ignore
        }
    };

    const shareTargets = [
        {
            label: "واتساب",
            href: `https://wa.me/?text=${encodeURIComponent(`${title} ${shareUrl}`)}`,
            Icon: WhatsAppIcon,
        },
        {
            label: "إكس",
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`,
            Icon: XIcon,
        },
    ];

    return (
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <h3 className="flex items-center gap-2 font-bold text-gray-900">
                <Share2 className="h-4 w-4 text-primary-600" />
                شارك المقال
            </h3>
            <div className="mt-3 flex items-center gap-2">
                {shareTargets.map(({ label, href, Icon }) => (
                    <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-primary-200 hover:text-primary-600"
                    >
                        <Icon className="h-4 w-4" />
                    </a>
                ))}
                <button
                    type="button"
                    onClick={copyLink}
                    aria-label="نسخ الرابط"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-primary-200 hover:text-primary-600"
                >
                    {copied ? (
                        <Check className="h-4 w-4 text-emerald-600" />
                    ) : (
                        <Link2 className="h-4 w-4" />
                    )}
                </button>
            </div>

            <button
                type="button"
                onClick={toggleSave}
                aria-pressed={saved}
                className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold transition-colors ${
                    saved
                        ? "border-primary-200 bg-primary-50 text-primary-700"
                        : "border-gray-200 text-gray-700 hover:border-primary-200 hover:text-primary-600"
                }`}
            >
                {saved ? (
                    <BookmarkCheck className="h-4 w-4" />
                ) : (
                    <Bookmark className="h-4 w-4" />
                )}
                {saved ? "محفوظ لديك" : "احفظ المقال لاحقاً"}
            </button>
        </div>
    );
};

const NewsletterCard = () => (
    <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <h3 className="font-bold text-gray-900">اشترك في نشرتنا</h3>
        <p className="mt-2 text-sm text-gray-500">
            احصل على أحدث المقالات والنصائح التسويقية مباشرة على بريدك.
        </p>
        <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-4 space-y-2"
        >
            <input
                type="email"
                required
                placeholder="بريدك الإلكتروني"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-700 focus:border-primary-400 focus:outline-none"
            />
            <button
                type="submit"
                className="w-full rounded-xl bg-primary-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-700"
            >
                اشترك الآن
            </button>
        </form>
    </div>
);

const WhatsAppCTACard = () => (
    <div className="rounded-2xl border border-gray-100 bg-white p-6 text-center shadow-sm">
        <h3 className="text-lg font-bold text-gray-900">جاهز للبدء؟</h3>
        <p className="mt-2 text-sm text-gray-500">
            دعنا نبني معاً استراتيجية تسويق رقمي تحقق أهدافك.
        </p>
        <a
            href={`https://api.whatsapp.com/send/?phone=${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 font-bold text-white transition-colors hover:bg-[#20bd5a]"
        >
            <WhatsAppIcon className="h-5 w-5" />
            <span>تواصل عبر واتساب</span>
        </a>
    </div>
);

// Decision-driving banner shown at the end of every article — same for all
// posts, not tied to any one article's content.
const DecisionCTABanner = () => (
    <div className="mt-12 overflow-hidden rounded-3xl bg-gradient-to-br from-primary-600 to-[#0B1F66] px-6 py-10 text-center sm:px-12 sm:py-14">
        <h2 className="text-2xl font-bold text-white sm:text-3xl">
            جاهز لاتخاذ القرار الصحيح لعملك؟
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-primary-100">
            فريقنا جاهز لمساعدتك على تحويل ما قرأته إلى خطة عملية تحقق نتائج
            حقيقية لأعمالك.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a
                href="/contact"
                className="flex items-center gap-2 rounded-xl bg-white px-6 py-3 font-bold text-primary-700 transition-colors hover:bg-primary-50"
            >
                احصل على استشارة مجانية
                <ArrowLeft className="h-4 w-4" />
            </a>
            <a
                href={`https://api.whatsapp.com/send/?phone=${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-xl bg-[#25D366] px-6 py-3 font-bold text-white transition-colors hover:bg-[#20bd5a]"
            >
                <WhatsAppIcon className="h-5 w-5" />
                تواصل عبر واتساب
            </a>
        </div>
    </div>
);

// Article tags from the post's own SEO fields (dashboard editor): the focus
// keyword as the lead chip, then its keywords. Each links to the blog search.
const ArticleTags = ({ focusKeyword, keywords }) => {
    const focus = typeof focusKeyword === "string" ? focusKeyword.trim() : "";
    const tags = [
        ...new Set(
            (Array.isArray(keywords) ? keywords : [])
                .map((k) => String(k).trim())
                .filter((k) => k && k !== focus)
        ),
    ].slice(0, 8);
    if (!focus && tags.length === 0) return null;

    const searchHref = (term) => `/blogs?search=${encodeURIComponent(term)}`;

    return (
        <div className="mt-5 flex flex-wrap items-center gap-2" aria-label="وسوم المقال">
            {focus && (
                <a
                    href={searchHref(focus)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-primary-700"
                >
                    <Tag className="h-3.5 w-3.5" aria-hidden="true" />
                    {focus}
                </a>
            )}
            {tags.map((tag) => (
                <a
                    key={tag}
                    href={searchHref(tag)}
                    className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:border-primary-200 hover:text-primary-600"
                >
                    #{tag}
                </a>
            ))}
        </div>
    );
};

const trackBlogCta = (placement, channel) => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "blog_cta_click", { placement, channel });
    }
};

// Splits the article HTML at its middle top-level <h2> so a CTA can sit
// between the two halves. Returns [html, null] (no split) when the post has
// fewer than 2 headings or the cut would land inside a block element.
const splitArticleBody = (html) => {
    if (!html) return [html, null];
    const starts = [];
    const re = /<h2[\s>]/gi;
    let m;
    while ((m = re.exec(html))) starts.push(m.index);
    if (starts.length < 2) return [html, null];

    const cut = starts[Math.floor(starts.length / 2)];
    const head = html.slice(0, cut);
    const balanced = ["div", "section", "ul", "ol", "table", "blockquote"].every(
        (tag) =>
            (head.match(new RegExp(`<${tag}[\\s>]`, "gi")) || []).length ===
            (head.match(new RegExp(`</${tag}>`, "gi")) || []).length
    );
    return balanced ? [head, html.slice(cut)] : [html, null];
};

// Mid-article consultation CTA — catches readers at peak engagement. Not an
// <h2> on purpose: the TOC and the section counter only read real headings.
const MidArticleConsultCTA = ({ title }) => {
    const message = `مرحباً، أرغب في استشارة مجانية بخصوص: ${title}`;
    return (
        <aside className="relative mt-8 mb-8 overflow-hidden rounded-3xl bg-gradient-to-br from-primary-600 to-[#0B1F66] p-5 shadow-xl sm:p-8 lg:mb-10 lg:p-10">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-16 -left-16 h-56 w-56 rounded-full bg-white/10 blur-3xl"
            />
            <div className="relative text-right">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-bold text-white">
                    <span className="h-2 w-2 rounded-full bg-[#25D366]" />
                    استشارة مجانية · بدون أي التزام
                </span>
                <p className="mt-4 text-2xl font-black leading-snug text-white sm:text-3xl">
                    اطلب استشارة مجانية الآن
                </p>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-blue-100/90 sm:text-base">
                    وصلت إلى منتصف المقال؟ دع فريقنا يحوّل هذه الأفكار إلى خطة
                    عملية تناسب نشاطك وميزانيتك.
                </p>
                <ul className="mt-4 grid gap-2 sm:grid-cols-3">
                    {["خطة مخصصة لنشاطك", "تواصل مباشر مع فريقنا", "بدون أي التزام"].map((item) => (
                        <li key={item} className="flex items-center gap-2 text-sm font-semibold text-white">
                            <CircleCheck className="h-4 w-4 shrink-0 text-green-400" />
                            {item}
                        </li>
                    ))}
                </ul>
                <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap sm:items-center">
                    <a
                        href={`https://api.whatsapp.com/send/?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(message)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackBlogCta("mid_article", "whatsapp")}
                        className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-3 font-bold text-white shadow-lg shadow-black/20 transition-colors hover:bg-[#20bd5a]"
                    >
                        <WhatsAppIcon className="h-5 w-5" />
                        اطلب استشارة مجانية الآن
                        <ArrowLeft className="h-4 w-4" />
                    </a>
                    <a
                        href="/contact"
                        onClick={() => trackBlogCta("mid_article", "contact_form")}
                        className="flex min-h-11 items-center justify-center rounded-xl border border-white/25 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/20"
                    >
                        أو عبر نموذج التواصل
                    </a>
                </div>
            </div>
        </aside>
    );
};

// WhatsApp banner closing the article body (before FAQ / related links) —
// same copy for every post. Text column only for now; the reference design
// has a photo on the left, to be added once the real image file exists.
const ArticleWhatsAppBanner = () => (
    <div className="mt-8 overflow-hidden rounded-3xl border border-primary-100 bg-gradient-to-l from-primary-50 to-white">
        <div className="flex items-center gap-8 p-5 sm:p-8 lg:p-10">
            <div className="flex-1 text-right">
                <p className="text-lg font-bold text-gray-900 sm:text-2xl">
                    هل لديك استفسار؟
                </p>
                <p className="mt-1 text-xl font-black leading-snug text-primary-600 sm:text-3xl">
                    تواصل معنا الآن عبر واتساب
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base">
                    يسعدنا الإجابة على جميع استفساراتك وتقديم الخطة المناسبة
                    لاحتياجات موقعك.
                </p>
                <a
                    href={`https://api.whatsapp.com/send/?phone=${WHATSAPP_NUMBER}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackBlogCta("article_end", "whatsapp")}
                    className="mt-5 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-3 font-bold text-white shadow-sm transition-colors hover:bg-[#20bd5a] sm:w-fit"
                >
                    <WhatsAppIcon className="h-5 w-5" />
                    تواصل معنا عبر واتساب الآن
                    <ArrowLeft className="h-4 w-4" />
                </a>
            </div>
            <div
                aria-hidden="true"
                className="hidden h-36 w-36 shrink-0 items-center justify-center rounded-full bg-green-50 sm:flex"
            >
                <WhatsAppIcon className="h-16 w-16 text-green-500" />
            </div>
        </div>
    </div>
);

// Thin progress bar tracking how far the reader has moved through the
// article body — gives immediate feedback on remaining reading length.
const ReadingProgressBar = ({ targetRef }) => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const el = targetRef.current;
            if (!el) return;
            const elTop = el.getBoundingClientRect().top + window.scrollY;
            const elHeight = el.offsetHeight;
            const scrolled =
                window.scrollY - elTop + window.innerHeight * 0.5;
            const pct = Math.min(
                100,
                Math.max(0, (scrolled / elHeight) * 100),
            );
            setProgress(pct);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, [targetRef]);

    return (
        <div className="fixed inset-x-0 top-0 z-[90] h-1 bg-transparent">
            <div
                className="h-full bg-gradient-to-r from-primary-500 to-[#00D4FF] transition-[width] duration-150 ease-out"
                style={{ width: `${progress}%` }}
            />
        </div>
    );
};

// Internal-linking block for the article — real service pages and real
// case-study cards matched to this post's category/title server-side
// (BlogController::relatedServiceLinks/relatedPortfolioLinks), never
// fabricated. Renders nothing for whichever half has no real matches.
const RelatedLinksSection = ({ services, portfolio }) => {
    const hasServices = services && services.length > 0;
    const hasPortfolio = portfolio && portfolio.length > 0;

    if (!hasServices && !hasPortfolio) return null;

    return (
        <div className="mt-10 space-y-8 border-t border-gray-100 pt-8">
            {hasServices && (
                <div>
                    <h2 className="mb-4 text-lg font-bold text-gray-900">خدمات ذات صلة</h2>
                    <div className="flex flex-wrap gap-2.5">
                        {services.map(({ label, href }) => (
                            <a
                                key={href}
                                href={href}
                                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                            >
                                {label}
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {hasPortfolio && (
                <div>
                    <h2 className="mb-4 text-lg font-bold text-gray-900">أعمال ذات صلة من معرض أعمالنا</h2>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
                        {portfolio.map((item) => (
                            <a
                                key={item.id}
                                href={item.href}
                                className="group block overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md"
                            >
                                <div className="aspect-square overflow-hidden bg-gray-100">
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        loading="lazy"
                                        decoding="async"
                                        width={480}
                                        height={480}
                                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                </div>
                                <p className="p-2.5 text-xs font-bold text-gray-800 line-clamp-2 sm:p-3 sm:text-sm">{item.title}</p>
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const BlogDetails = ({ blog, relatedBlogs, otherBlogs, relatedServices, relatedPortfolio }) => {
    const bodyRef = useRef(null);
    const hydrated = useHydrated();
    const isMobile = useIsMobile();

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("ar-EG", {
            timeZone: "Asia/Riyadh",
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    };

    // Strips markup so both the word count and reading-time estimate below
    // are based on actual body text, not HTML tag noise.
    const plainTextWordCount = (content) => {
        if (!content) return 0;
        const text = content.replace(/<[^>]*>/g, " ").trim();
        return text === "" ? 0 : text.split(/\s+/).filter(Boolean).length;
    };

    // Calculate read time based on content length
    const calculateReadTime = (words) => {
        if (!words) return "5 دقائق";
        const wordsPerMinute = 200;
        const minutes = Math.ceil(words / wordsPerMinute);
        return `${minutes} دقيقة`;
    };

    const wordCount = plainTextWordCount(blog?.content);

    // A few published posts have a thumbnail path in the DB whose file no
    // longer exists on disk (upload never persisted) — the `thumbnail ?
    // <img> : <BlogHeroFallback>` check below only catches a null path, not
    // a 404 at request time, so this flips it to the same branded fallback
    // once the <img> actually fails to load. Real cover images for those
    // posts still need re-uploading via the dashboard.
    const [heroImgError, setHeroImgError] = useState(false);

    // Format blog data
    const currentBlog = {
        id: blog?.id,
        title: blog?.title,
        category: blog?.category,
        date: formatDate(blog?.publish_date),
        updatedDate: formatDate(blog?.updated_at || blog?.publish_date),
        readTime: calculateReadTime(wordCount),
        wordCount,
        views: blog?.views ?? 0,
        thumbnail: blog?.thumbnail || null,
        author: {
            name: blog?.author || "فريق التحرير",
            avatar:
                KNOWN_AUTHOR_PHOTOS[blog?.author] ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    blog?.author || "Team",
                )}&background=084FC7&color=fff&size=256&bold=true&font-size=0.4`,
            role: "خبير التسويق الرقمي",
        },
        body:
            blog?.content ||
            `
        <p>في عالم التسويق الرقمي المتغير باستمرار، أصبح بناء استراتيجية تسويقية فعّالة أمرًا حيويًا لنجاح أي مشروع أو علامة تجارية. لم يعد الأمر يقتصر على مجرد وجود حضور على وسائل التواصل الاجتماعي أو موقع إلكتروني، بل يتطلب نهجًا شاملاً ومدروسًا.</p>

        <h2>فهم جمهورك المستهدف</h2>
        <p>قبل البدء بأي حملة تسويقية، يجب أن تعرف بدقة من هو جمهورك المستهدف. استخدم أدوات تحليل البيانات لفهم:</p>
        <ul>
            <li>الخصائص الديموغرافية للجمهور</li>
            <li>اهتماماتهم وسلوكهم الشرائي</li>
            <li>المنصات التي يستخدمونها</li>
            <li>التحديات والمشاكل التي يواجهونها</li>
        </ul>

        <h2>تحديد أهداف قابلة للقياس</h2>
        <p>حدد أهدافًا واضحة وقابلة للقياس باستخدام منهجية SMART:</p>
        <ul>
            <li><strong>محددة (Specific):</strong> كن دقيقًا في تحديد ما تريد تحقيقه</li>
            <li><strong>قابلة للقياس (Measurable):</strong> استخدم مؤشرات أداء واضحة</li>
            <li><strong>قابلة للتحقيق (Achievable):</strong> كن واقعيًا في توقعاتك</li>
            <li><strong>ذات صلة (Relevant):</strong> تأكد من توافق الأهداف مع رؤية الشركة</li>
            <li><strong>محددة زمنيًا (Time-bound):</strong> ضع إطارًا زمنيًا واضحًا</li>
        </ul>

        <h2>اختيار القنوات التسويقية المناسبة</h2>
        <p>ليست كل القنوات التسويقية مناسبة لكل مشروع. ركّز على القنوات التي يتواجد فيها جمهورك:</p>
        <ul>
            <li>وسائل التواصل الاجتماعي (فيسبوك، إنستغرام، تويتر، لينكد إن)</li>
            <li>التسويق عبر محركات البحث (SEO & SEM)</li>
            <li>التسويق بالمحتوى والمدونات</li>
            <li>التسويق عبر البريد الإلكتروني</li>
            <li>التسويق بالفيديو (يوتيوب، تيك توك)</li>
        </ul>

        <h2>إنشاء محتوى قيّم ومتنوع</h2>
        <p>المحتوى هو الملك في عالم التسويق الرقمي. قدم قيمة حقيقية لجمهورك من خلال:</p>
        <ul>
            <li>مقالات تعليمية ومفيدة</li>
            <li>فيديوهات توضيحية</li>
            <li>إنفوجرافيك جذاب</li>
            <li>دراسات حالة وقصص نجاح</li>
            <li>محتوى تفاعلي (استطلاعات، كويزات)</li>
        </ul>

        <h2>قياس الأداء والتحسين المستمر</h2>
        <p>استخدم أدوات التحليل لقياس نتائج حملاتك التسويقية:</p>
        <ul>
            <li>Google Analytics لتحليل حركة المرور</li>
            <li>Facebook Insights لقياس التفاعل</li>
            <li>أدوات SEO لمتابعة ترتيب الكلمات المفتاحية</li>
            <li>معدلات التحويل والمبيعات</li>
        </ul>

        <p>قم بمراجعة البيانات بشكل دوري وعدّل استراتيجيتك بناءً على النتائج. التسويق الرقمي الناجح هو عملية مستمرة من التجربة والتعلم والتحسين.</p>

        <h2>الخلاصة</h2>
        <p>بناء استراتيجية تسويق رقمي ناجحة يتطلب وقتًا وجهدًا، لكن النتائج تستحق الاستثمار. ابدأ بخطوات صغيرة، وكن صبورًا، وتذكر أن النجاح في التسويق الرقمي هو رحلة طويلة وليست وجهة نهائية.</p>
    `,
    };

    const [bodyFirstHalf, bodySecondHalf] = splitArticleBody(currentBlog.body);

    // Format related blogs for display
    const formattedRelatedBlogs =
        relatedBlogs?.map((blog) => ({
            ...blog,
            description: blog.excerpt,
            date: formatDate(blog.publish_date),
            thumbnail:
                blog.thumbnail ||
                `https://picsum.photos/id/${1025 + blog.id}/400/300`,
            categoryColor: blog.category_color || "bg-blue-500",
        })) || [];

    // Format other blogs for display
    const formattedOtherBlogs =
        otherBlogs?.map((blog) => ({
            ...blog,
            description: blog.excerpt,
            date: formatDate(blog.publish_date),
            thumbnail:
                blog.thumbnail ||
                `https://picsum.photos/id/${1055 + blog.id}/600/400`,
            categoryColor: blog.category_color || "bg-blue-500",
        })) || [];

    return (
        <div className="wrapper pb-4 lg:pb-8">
            <ReadingProgressBar targetRef={bodyRef} />

            {/* BlogPosting/WebPage/FAQPage/Person structured data is rendered
                centrally by SeoHead.jsx (mounted in WebsiteLayout) from the
                seo.schema prop BlogController::show() builds via
                App\Support\BlogSeoSchema — kept out of this component so
                there is exactly one JSON-LD source per page, not two. */}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Main Content — header, hero image and trust cards live
                    inside this column so the sidebar runs the full height
                    alongside them, starting from the very top. */}
                <motion.article
                    className="lg:col-span-9"
                    // Mobile gets prerendered markup: start visible so the article paints
                    // with the HTML instead of after the JS bundle (LCP).
                    initial={isMobile ? false : { opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                >
                    {/* Article header — tag → title → description → meta,
                        right-aligned like a natural article heading (not a
                        centered landing-page hero). */}
                    <div className="text-right">
                        <span className="mb-6 inline-flex items-center rounded-full bg-primary-50 px-4 py-1.5 text-xs sm:text-sm font-semibold text-primary-600">
                            {currentBlog.category}
                        </span>
                        <h1 className="max-w-[850px] text-[28px] font-extrabold leading-[1.2] text-gray-900 sm:text-4xl lg:text-[42px]">
                            {currentBlog.title}
                        </h1>
                        {blog?.excerpt && (
                            <p className="mt-6 max-w-[720px] text-base text-gray-500 sm:text-lg">
                                {blog.excerpt}
                            </p>
                        )}

                        <div className="mt-8 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 text-sm text-gray-500">
                            <span className="flex items-center gap-2.5">
                                <img
                                    src={currentBlog.author.avatar}
                                    alt={currentBlog.author.name}
                                    loading="lazy"
                                    className="h-9 w-9 rounded-full object-cover ring-2 ring-white shadow-sm"
                                />
                                <span className="text-right leading-tight">
                                    <span className="block font-bold text-gray-800">
                                        {currentBlog.author.name}
                                    </span>
                                    <span className="block text-xs text-gray-400">
                                        {currentBlog.author.role}
                                    </span>
                                </span>
                            </span>
                            <span className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                <span className="flex items-center gap-1.5">
                                    <Calendar className="h-4 w-4" aria-hidden="true" />
                                    نُشر: {currentBlog.date}
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <CalendarClock className="h-4 w-4" aria-hidden="true" />
                                    آخر تحديث: {currentBlog.updatedDate}
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Clock className="h-4 w-4" aria-hidden="true" />
                                    {currentBlog.readTime} قراءة
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <FileText className="h-4 w-4" aria-hidden="true" />
                                    {currentBlog.wordCount.toLocaleString("ar-EG")} كلمة
                                </span>
                                {/* Fixed width: the count only appears after hydration, so a
                                    growing span would re-wrap this row (layout shift). */}
                                <span className="flex items-center gap-1.5" style={{ minWidth: "8.5em" }}>
                                    <Eye className="h-4 w-4" aria-hidden="true" />
                                    {hydrated ? currentBlog.views.toLocaleString("ar-EG") : "—"} مشاهدة
                                </span>
                                <button
                                type="button"
                                onClick={() => {
                                    if (navigator.share) {
                                        navigator.share({
                                            title: currentBlog.title,
                                            url: window.location.href,
                                        });
                                    } else {
                                        navigator.clipboard?.writeText(window.location.href);
                                    }
                                }}
                                aria-label="مشاركة المقال"
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-primary-200 hover:text-primary-600"
                            >
                                    <Share2 className="h-4 w-4" />
                                </button>
                            </span>
                        </div>

                    </div>

                    {/* Hero image — fixed 16:9 (1600x900) crop for every blog,
                        regardless of the source image's real dimensions.
                        Falls back to a branded panel (logo + title) when no
                        thumbnail was uploaded, instead of a random stock
                        photo. Always below the title/meta, never beside it. */}
                    <div className="mt-10 aspect-[1600/900] w-full overflow-hidden rounded-[28px] shadow-[0_20px_50px_-20px_rgba(15,23,42,0.15)]">
                        {currentBlog.thumbnail && !heroImgError ? (
                            <img
                                src={currentBlog.thumbnail}
                                alt={currentBlog.title}
                                width={1600}
                                height={900}
                                className="h-full w-full object-cover"
                                loading="eager"
                                decoding="async"
                                fetchPriority="high"
                                onError={() => setHeroImgError(true)}
                            />
                        ) : (
                            <BlogHeroFallback
                                title={currentBlog.title}
                                description={blog?.excerpt}
                            />
                        )}
                    </div>

                    {/* Trust cards — immediately below the cover image */}
                    <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
                        {QUICK_FACTS.map(({ Icon, label, sub }) => (
                            <div
                                key={label}
                                className="flex h-[88px] items-center gap-3 rounded-[20px] border border-gray-100 bg-gray-50/70 px-4"
                            >
                                <Icon className="h-5 w-5 shrink-0 text-primary-600" />
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-bold text-gray-800">
                                        {label}
                                    </p>
                                    <p className="truncate text-xs text-gray-500">
                                        {sub}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10">
                        <TableOfContents containerRef={bodyRef} />
                    </div>

                    <KeyPointsCallout points={blog?.key_points} />

                    {/* Blog Body */}
                    <div
                        ref={bodyRef}
                        className="blog-article-body border rounded-lg overflow-hidden bg-gray-50 border-gray-200 p-4 lg:p-12"
                    >
                        <div
                            className="ProseMirror prose prose-sm sm:prose lg:prose-lg xl:prose-xl max-w-none focus:outline-none min-h-[300px]"
                            dir="auto"
                            dangerouslySetInnerHTML={{ __html: bodyFirstHalf }}
                        />
                        {bodySecondHalf && (
                            <>
                                <MidArticleConsultCTA title={currentBlog.title} />
                                <div
                                    className="ProseMirror prose prose-sm sm:prose lg:prose-lg xl:prose-xl max-w-none focus:outline-none"
                                    dir="auto"
                                    dangerouslySetInnerHTML={{ __html: bodySecondHalf }}
                                />
                            </>
                        )}
                    </div>

                    <ArticleWhatsAppBanner />

                    {/* FAQ Section */}
                    {blog?.faq && blog.faq.length > 0 && (
                        <FaqSection faqs={blog.faq} />
                    )}

                    <div className="mt-8 border-t border-gray-100 pt-6">
                        <ArticleTags
                            focusKeyword={blog?.seo_data?.focusKeyword}
                            keywords={blog?.seo_data?.keywords}
                        />
                    </div>

                    <RelatedLinksSection services={relatedServices} portfolio={relatedPortfolio} />
                </motion.article>

                {/* Sidebar */}
                <motion.aside
                    className="lg:col-span-3"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                >
                    <div className="sticky top-24 space-y-5">
                        <AuthorCard author={currentBlog.author} />

                        <GrowthCTA />

                        {formattedRelatedBlogs.length > 0 && (
                            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
                                <h3 className="mb-4 font-bold text-gray-900">
                                    مقالات ذات صلة
                                </h3>
                                {/* 2-up below lg (sidebar stacks under the article
                                    there); single column in the desktop sidebar. */}
                                <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:block lg:space-y-4">
                                    {formattedRelatedBlogs.map((blog, index) => (
                                        <BlogCard
                                            key={blog.id}
                                            blog={blog}
                                            index={index}
                                            dense
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <ShareAndSaveCard title={currentBlog.title} />

                        <NewsletterCard />

                        <WhatsAppCTACard />
                    </div>
                </motion.aside>
            </div>

            <DecisionCTABanner />

            {/* Other Blogs Section */}
            <motion.section
                className="mt-16"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            >
                <div className="mb-8 flex items-center justify-between">
                    <h3 className="text-base lg:text-2xl font-bold text-gray-900">
                        مقالات أخرى قد تعجبك
                    </h3>
                    <a
                        href="/blogs"
                        className="flex items-center gap-2 text-primary-600 font-semibold transition-all hover:gap-3 hover:text-primary-700 text-sm lg:text-base"
                    >
                        <span>عرض الكل</span>
                        <ArrowLeft className="h-5 w-5" />
                    </a>
                </div>

                <div className="relative">
                    <Swiper
                        modules={[Autoplay, Navigation]}
                        spaceBetween={24}
                        slidesPerView={1.5}
                        navigation={{
                            nextEl: ".swiper-button-next-custom",
                            prevEl: ".swiper-button-prev-custom",
                        }}
                        autoplay={{
                            delay: 4000,
                            disableOnInteraction: false,
                        }}
                        breakpoints={{
                            640: { slidesPerView: 3 },
                            1024: { slidesPerView: 4 },
                        }}
                        className="!pb-4"
                    >
                        {formattedOtherBlogs.map((blog, index) => (
                            <SwiperSlide key={blog.id}>
                                <BlogCard blog={blog} index={index} />
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {/* Custom Navigation Buttons */}
                    <button
                        className="swiper-button-prev-custom absolute -left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg transition-all hover:bg-primary-600 hover:text-white disabled:opacity-50"
                        aria-label="Previous slide"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <button
                        className="swiper-button-next-custom absolute -right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg transition-all hover:bg-primary-600 hover:text-white disabled:opacity-50"
                        aria-label="Next slide"
                    >
                        <ArrowRight className="h-5 w-5" />
                    </button>
                </div>
            </motion.section>
        </div>
    );
};

export default BlogDetails;
